import { useState, useRef, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { useMagnum } from '@/context/MagnumContext'
import { apiFetch } from '@/lib/apiFetch'
import { useExerciseSession, NO_ATTEMPT_TIMEOUT, type SessionCredit } from '@/hooks/useExerciseSession'
import { fetchMagnumMoment, EffectData } from '@/lib/magnumMoment'
import EffectRenderer from '@/components/EffectRenderer'
import MagnumSays from '@/components/MagnumSays'
import MusicStaff from '@/components/MusicStaff'
import { useMusicPlayback } from '@/hooks/useMusicPlayback'
import { generateNotes, noteTierForLevel, LETTERS, type GeneratedNote } from '@/lib/noteNaming'
import type { ClefPreference } from '@/lib/musicTheory'

type GameState = 'loading' | 'playing' | 'result' | 'done' | 'timedout' | 'error'

const QUESTIONS_PER_SESSION = 20
const STREAK_MILESTONES = [5, 10, 15, 20]
/** Right answers move on quickly; a miss holds long enough to read the real letter. */
const ADVANCE_CORRECT_MS = 700
const ADVANCE_WRONG_MS = 1800
/**
 * Reading a note is quick, but a kid stuck on a ledger line deserves room to think.
 * This matches the server's NOTE_NAMING credit gap, so the round we stop here is the
 * same one the backend would have stopped counting anyway.
 */
const ATTEMPT_TIMEOUT_MS = 90_000

export default function NoteNamingPage() {
  const { user } = useAuth()
  const magnum = useMagnum()
  const [timedOut, setTimedOut] = useState(false)
  const { sessionId, stats, activeMs, isIdle, startSession, endSession, recordAttempt, dismissIdle } =
    useExerciseSession({
      exerciseType: 'NOTE_NAMING',
      attemptTimeoutMs: ATTEMPT_TIMEOUT_MS,
      onAttemptTimeout: () => setTimedOut(true),
    })
  const { playInterval, isPlaying } = useMusicPlayback()

  const [state, setState] = useState<GameState>('loading')
  const [notes, setNotes] = useState<GeneratedNote[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [chosenLetter, setChosenLetter] = useState<string | null>(null)
  const [feedback, setFeedback] = useState<{ correct: boolean } | null>(null)
  const [activeEffects, setActiveEffects] = useState<EffectData[] | null>(null)
  const [credit, setCredit] = useState<SessionCredit | null>(null)
  const questionStartRef = useRef(Date.now())
  // Ticks once a second; held in a ref so it can't churn the keydown listener.
  const activeMsRef = useRef(activeMs)
  activeMsRef.current = activeMs

  const currentNote = notes[currentIndex] || null

  useEffect(() => {
    magnum.setFrameIdle(isIdle)
    if (isIdle) {
      fetchMagnumMoment({
        contextType: 'IDLE_RETURN',
        exerciseType: 'NOTE_NAMING',
        category: 'MUSIC',
        streak: stats.streak,
        sessionId,
        activeMs,
      })
        .then((m) => magnum.handleEngineResponse(m))
        .catch(() => {})
    }
  }, [isIdle])

  const startGame = async () => {
    setState('loading')
    try {
      // Settings first: a session opened before a failing fetch would be orphaned,
      // still heartbeating, with no way back to it.
      const [settings, diffData] = await Promise.all([
        apiFetch('/music/settings/mine'),
        apiFetch('/game-difficulty/mine/NOTE_NAMING'),
      ])
      const newSessionId = await startSession()
      const clefPreference: ClefPreference = settings.clefPreference || 'TREBLE'
      setNotes(generateNotes(QUESTIONS_PER_SESSION, noteTierForLevel(diffData.difficultyLevel), clefPreference))
      setCurrentIndex(0)
      setChosenLetter(null)
      setFeedback(null)
      setCredit(null)
      setTimedOut(false)
      setState('playing')
      questionStartRef.current = Date.now()

      fetchMagnumMoment({
        contextType: 'GAME_STARTED',
        exerciseType: 'NOTE_NAMING',
        category: 'MUSIC',
        streak: 0,
        sessionId: newSessionId,
      })
        .then((m) => magnum.handleEngineResponse(m, 'happy'))
        .catch(() => {})
    } catch {
      setState('error')
    }
  }

  const handleAnswer = useCallback(
    async (letter: string) => {
      const note = notes[currentIndex]
      if (!note || state !== 'playing') return

      const responseMs = Date.now() - questionStartRef.current
      const correct = letter === note.letter

      const newStreak = correct ? stats.streak + 1 : 0
      const isMilestone = correct && STREAK_MILESTONES.includes(newStreak)
      const contextType = correct
        ? isMilestone
          ? 'STREAK_MILESTONE'
          : 'CORRECT_ANSWER'
        : 'WRONG_ANSWER'

      setChosenLetter(letter)
      setFeedback({ correct })
      setState('result')

      fetchMagnumMoment({
        contextType,
        exerciseType: 'NOTE_NAMING',
        category: 'MUSIC',
        correct,
        responseMs,
        activeMs: activeMsRef.current,
        streak: newStreak,
        sessionId,
      })
        .then((moment) => {
          magnum.handleEngineResponse(moment, correct ? 'happy' : 'encouraging')
          if (moment.effects?.length) setActiveEffects(moment.effects)
        })
        .catch(() => {})

      await recordAttempt(null, correct, responseMs, {
        noteName: note.fullName,
        playerAnswer: letter,
        clef: note.clef,
      })
    },
    [notes, currentIndex, state, stats.streak, sessionId, recordAttempt],
  )

  const nextQuestion = () => {
    const next = currentIndex + 1
    if (next >= notes.length) {
      setState('done')

      fetchMagnumMoment({
        contextType: 'SESSION_END',
        exerciseType: 'NOTE_NAMING',
        category: 'MUSIC',
        streak: stats.streak,
        sessionId,
        activeMs,
      })
        .then((m) => magnum.handleEngineResponse(m, 'excited'))
        .catch(() => {})

      endSession().then(setCredit)
    } else {
      setCurrentIndex(next)
      setChosenLetter(null)
      setFeedback(null)
      setState('playing')
      questionStartRef.current = Date.now()
    }
  }

  // Typing the letter is the whole game — the buttons below are the touch fallback.
  useEffect(() => {
    if (state !== 'playing') return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return
      // A held key auto-repeats, and the listener comes back for the next note
      // while it is still down — without this one press answers the whole round.
      if (e.repeat) return
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      const letter = e.key.toUpperCase()
      if (!LETTERS.includes(letter as (typeof LETTERS)[number])) return
      e.preventDefault()
      handleAnswer(letter)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [state, handleAnswer])

  useEffect(() => {
    if (state === 'result') {
      const timer = setTimeout(nextQuestion, feedback?.correct ? ADVANCE_CORRECT_MS : ADVANCE_WRONG_MS)
      return () => clearTimeout(timer)
    }
  }, [state, currentIndex])

  // Ninety seconds with no answer ends the round, and its time stops counting
  // toward screen time — otherwise leaving the game open is as good as playing.
  useEffect(() => {
    if (!timedOut || state === 'timedout' || state === 'done' || state === 'error') return
    setState('timedout')
    endSession(NO_ATTEMPT_TIMEOUT).then(setCredit)
  }, [timedOut])

  useEffect(() => {
    startGame()
  }, [])

  useEffect(() => {
    if (isIdle && state === 'result') dismissIdle()
  }, [state])

  if (!user || user.type !== 'child') return null

  if (state === 'loading') {
    return (
      <div className="py-16 text-center">
        <p className="text-lg text-muted-foreground">Loading...</p>
      </div>
    )
  }

  if (state === 'timedout') {
    return (
      <div className="py-8 text-center space-y-6">
        <MagnumSays variant="warning">
          I stopped the round &mdash; the notes went quiet for a while there. Time only counts
          toward screen time while you&apos;re actually naming them. Ready for another go?
        </MagnumSays>
        <div className="flex gap-4 justify-center pt-2">
          <button
            onClick={startGame}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-xl font-bold hover:opacity-90 transition"
          >
            Play Again
          </button>
          <Link
            to="/practice"
            className="px-6 py-3 border border-border rounded-xl font-bold hover:bg-accent transition inline-flex items-center"
          >
            Back to Exercises
          </Link>
        </div>
      </div>
    )
  }

  if (state === 'error') {
    return (
      <div className="py-16 text-center space-y-6">
        <MagnumSays variant="warning">The staff didn&apos;t load. Not your fault &mdash; give it another go?</MagnumSays>
        <button
          onClick={startGame}
          className="px-6 py-3 bg-primary text-primary-foreground rounded-xl font-bold hover:opacity-90 transition"
        >
          Try Again
        </button>
      </div>
    )
  }

  if (state === 'done') {
    const accuracy = stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0
    return (
      <div className="py-8 text-center space-y-8">
        <div className="text-6xl">{credit && !credit.counted ? '🎼' : '🎵'}</div>
        <h1 className="text-3xl font-bold">Session Complete!</h1>
        {credit && !credit.counted && (
          <div className="max-w-sm mx-auto text-left">
            <MagnumSays variant="warning">
              {credit.reason === 'BELOW_ACCURACY'
                ? "This one doesn't count toward your screen time \u2014 too many notes went the wrong way. Look at the clef first, find middle C, and the next round will count! \ud83d\udcaa"
                : "This one doesn't count toward your screen time \u2014 I need a few more notes named before it counts. Give it another go! \ud83d\udcaa"}
            </MagnumSays>
          </div>
        )}
        <div className="grid grid-cols-3 gap-4 max-w-sm mx-auto">
          <div className="p-4 border border-border rounded-lg">
            <p className="text-2xl font-bold text-primary">{stats.correct}</p>
            <p className="text-xs text-muted-foreground">Correct</p>
          </div>
          <div className="p-4 border border-border rounded-lg">
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-xs text-muted-foreground">Total</p>
          </div>
          <div className="p-4 border border-border rounded-lg">
            <p className="text-2xl font-bold text-primary">{accuracy}%</p>
            <p className="text-xs text-muted-foreground">Accuracy</p>
          </div>
        </div>
        <div className="flex gap-4 justify-center pt-4">
          <button
            onClick={startGame}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-xl font-bold hover:opacity-90 transition"
          >
            Play Again
          </button>
          <Link
            to="/practice"
            className="px-6 py-3 border border-border rounded-xl font-bold hover:bg-accent transition inline-flex items-center"
          >
            Back to Exercises
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="text-center space-y-6">
      <EffectRenderer effects={activeEffects} onDone={() => setActiveEffects(null)} />

      <div className="flex items-center justify-between">
        <Link to="/practice" className="text-sm text-muted-foreground hover:text-foreground transition">
          &larr; Back
        </Link>
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span>
            {currentIndex + 1} of {notes.length}
          </span>
          <span className="text-primary font-medium">{stats.correct} correct</span>
          {stats.streak >= 3 && <span className="text-amber-500 font-medium">🔥 {stats.streak}</span>}
        </div>
      </div>

      <div className="w-full bg-accent rounded-full h-2">
        <div
          className="bg-primary h-2 rounded-full transition-all duration-300"
          style={{
            width: `${((currentIndex + (state === 'result' ? 1 : 0)) / notes.length) * 100}%`,
          }}
        />
      </div>

      {currentNote && (
        <>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">Type the letter of this note.</p>
            <div className="max-w-xs mx-auto bg-white rounded-xl p-4 border border-border">
              <MusicStaff abc={currentNote.abc} width={140} />
            </div>
            <div className="flex items-center justify-center mt-3">
              <button
                onClick={() => playInterval(currentNote.abc)}
                disabled={isPlaying}
                className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-accent transition disabled:opacity-50"
              >
                {isPlaying ? '🔊 Playing...' : '🔊 Hear It'}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-4 sm:grid-cols-7 gap-3 max-w-md mx-auto">
            {LETTERS.map((letter) => {
              let btnClass =
                'min-h-[56px] rounded-xl text-2xl font-bold transition border-2 flex items-center justify-center '
              if (state === 'result') {
                if (letter === currentNote.letter) {
                  btnClass += 'bg-green-100 border-green-500 text-green-800'
                } else if (letter === chosenLetter) {
                  btnClass += 'bg-red-100 border-red-500 text-red-800'
                } else {
                  btnClass += 'border-border text-muted-foreground opacity-50'
                }
              } else {
                btnClass += 'border-border hover:border-primary hover:bg-accent'
              }

              return (
                <button
                  key={letter}
                  onClick={() => handleAnswer(letter)}
                  disabled={state === 'result'}
                  className={btnClass}
                >
                  {letter}
                </button>
              )
            })}
          </div>

          {state === 'result' && feedback && (
            <div className={`text-lg font-semibold ${feedback.correct ? 'text-primary' : 'text-amber-500'}`}>
              {feedback.correct ? '✓ Correct!' : `✗ That one was ${currentNote.letter}`}
            </div>
          )}
        </>
      )}
    </div>
  )
}
